import {
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfISOWeek,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';
import times from 'lodash/times';
import { MeteorObservable } from 'meteor-rxjs';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Jobs } from 'server/shared/collections/jobs';
import { Department } from 'server/shared/models/department';
import { DepartmentPermissions } from 'server/shared/models/dispoPermissions';
import { Job } from 'server/shared/models/job';
import { JobRequestTabId } from 'server/shared/models/JobRequestTabId.enum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { Shift } from 'server/shared/models/shift';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { getWeekDayEnumByIndex } from 'server/shared/models/weekday.enum';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { CustomerService } from 'src/app/services/customer.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { mapToEnumWithFallback } from 'src/helpers/filterEnumValue';
import {
  Column,
  ColumnResizeManager,
  defaultJobRequestColumns,
} from 'src/helpers/job/jobColumns';
import { queryParam$ } from 'src/helpers/queryParam$';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { rxNgZoneRun } from '../../../helpers/rxNgZoneRun';
import { OrganisationService } from '../../services/organisation.service';
import { UserService } from '../../services/user.service';

export type JobRequestMonth = {
  weeks: JobRequestWeek[];
  startDate: Date;
  endDate: Date;
  collapsed: boolean;
};

export type JobRequestWeek = {
  dates: string[];
  startDate: Date;
  endDate: Date;
};

export type JobRequestTab = {
  label: string;
  isGlobalLabel?: boolean;
  value: JobRequestTabId;
  svgIcon?: string;
};

@Component({
  selector: 'job-requests',
  templateUrl: 'job-requests.html',
  styleUrls: [
    'job-requests.scss',
    '../../../stylesheets/components/atoms/jobs-timeline.scss',
    '../../../stylesheets/components/atoms/jobs-list-view.scss',
  ],
})
export class JobRequestsComponent implements OnInit, OnDestroy {
  private _monthsInFuture: number = 3;
  public months: JobRequestMonth[] = [];
  JobRequestTabId = JobRequestTabId;

  shiftsOfDepartment: {
    [weekDay: string]: { shift: ShiftEnum; shifts: Shift[] }[];
  } = {};

  shiftEnum = ShiftEnum;

  approveTab: JobRequestTab = {
    label: 'new',
    isGlobalLabel: true,
    value: JobRequestTabId.APPROVE,
    svgIcon: 'inbox',
  };

  requestTab: JobRequestTab = {
    label: 'request',
    value: JobRequestTabId.REQUEST,
    svgIcon: 'job-request',
  };
  loggedInUser$ = this.userService.readyDispoOrRequester$;
  dispoCustomer$ = this.customerService.readyDispoCustomer$;
  dispoPermissions$ = this.userService.dispoPermissions$;

  // Filter infrastructure
  jobTypeParam$ = queryParam$(this.route, 'jobType');
  shiftParam$ = queryParam$(this.route, 'shift');
  demandReasonParam$ = queryParam$(this.route, 'demandReason');

  shiftQuery$ = combineLatest([this.shiftParam$]).pipe(
    map(([shiftParam]) => {
      if (!shiftParam || shiftParam === 'ALL') {
        return undefined;
      }
      const shifts = shiftParam
        .split(',')
        .map((shift) => ShiftEnum[shift as keyof typeof ShiftEnum]);
      // Add shift filter if not 'ALL'
      return {
        'shifts.shift': { $in: shifts },
      };
    }),
  );

  jobTypeQuery$ = combineLatest([this.jobTypeParam$]).pipe(
    map(([jobTypeParam]) => {
      if (!jobTypeParam || jobTypeParam === 'ALL') {
        return undefined;
      }
      const jobTypes = jobTypeParam
        .split(',')
        .map((jobType) => JobTypeEnum[jobType as keyof typeof JobTypeEnum]);
      return { jobType: { $in: jobTypes } };
    }),
  );

  demandReasonQuery$ = combineLatest([
    this.demandReasonParam$,
    this.dispoCustomer$,
  ]).pipe(
    map(([demandReasonParam, customer]) => {
      if (!demandReasonParam || demandReasonParam === 'ALL') {
        return undefined;
      }
      return {
        [`customerInternalInformation.${customer._id}.demandReason.id`]:
          demandReasonParam,
      };
    }),
  );

  filterQuery$ = combineLatest([
    this.jobTypeQuery$,
    this.shiftQuery$,
    this.demandReasonQuery$,
  ]).pipe(
    map(([jobTypeQuery, shiftQuery, demandReasonQuery]) => {
      const $andQuery: Mongo.Selector<Job>[] = [];

      if (jobTypeQuery) {
        $andQuery.push(jobTypeQuery);
      }

      if (shiftQuery) {
        $andQuery.push(shiftQuery);
      }

      // Add demandReason filter if not 'ALL'
      if (demandReasonQuery) {
        $andQuery.push(demandReasonQuery);
      }
      return $andQuery.length ? $andQuery : undefined;
    }),
  );

  canApproveJobRequests$ = combineLatest([
    this.dispoCustomer$,
    // start with undefined because it will not emit for requester role
    this.dispoPermissions$.pipe(startWith(undefined)),
  ]).pipe(
    map(
      ([dispoCustomer, dispoPermissions]) =>
        dispoPermissions?.dispoAccess?.featurePermissions
          ?.canApproveJobRequests,
    ),
    distinctUntilChanged(),
    shareReplay(1),
  );

  tabs$ = this.canApproveJobRequests$.pipe(
    map((canApproveJobRequests) => {
      const tabs: JobRequestTab[] = [this.requestTab];
      if (canApproveJobRequests) {
        tabs.unshift(this.approveTab);
      }
      // not displaying tabs at all if there is just one tab
      return tabs.length > 1 ? tabs : [];
    }),
  );

  filtersCollapsed$ = new BehaviorSubject<boolean>(
    localStorage.getItem('jobRequestFiltersCollapsed') === 'true',
  );

  filterCounts$ = combineLatest([
    this.jobTypeParam$,
    this.shiftParam$,
    this.demandReasonParam$,
  ]).pipe(
    map(([jobType, shift, demandReason]) => {
      let count = 0;
      if (jobType && jobType !== 'ALL') {
        count++;
      }
      if (shift && shift !== 'ALL') {
        count++;
      }
      if (demandReason && demandReason !== 'ALL') {
        count++;
      }
      return count;
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );

  tabParam$: Observable<string> = queryParam$(this.route, 'tab');

  activeTabId$ = combineLatest([
    this.tabParam$,
    this.canApproveJobRequests$,
  ]).pipe(
    map(([tabParam, canApproveJobRequests]) => {
      return mapToEnumWithFallback(
        tabParam,
        JobRequestTabId,
        canApproveJobRequests
          ? JobRequestTabId.APPROVE
          : JobRequestTabId.REQUEST,
      );
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );

  openJobRequests$ = this.filterQuery$.pipe(
    switchMap((filterQuery) => {
      const query: Mongo.Selector<Job> = {
        $or: [
          {
            status: JobStatusEnum.JOB_REQUESTED,
          },
          {
            requestRetractionRequestedAt: { $exists: true },
            status: { $nin: [JobStatusEnum.CANCELED, JobStatusEnum.ARCHIVED] },
          },
        ],

        jobRequestRejected: { $exists: false },
      };
      if (filterQuery) {
        query.$and = filterQuery;
      }
      return Jobs.find(query, { sort: { createdAt: -1 } });
    }),
    rxNgZoneRun(this.ngZone),
    shareReplay(1),
  );

  selectedDepartment$ = new BehaviorSubject<Department | undefined>(undefined);

  jobRequestQuery$ = combineLatest([
    this.selectedDepartment$,
    this.filterQuery$,
  ]).pipe(
    map(([department, filterQuery]) => {
      if (!department) {
        // department is needed for job requests tab
        return undefined;
      }
      const query: Mongo.Selector<Job> = {
        'department.id': department.id,
        date: { $gte: format(startOfDay(new Date()), dbDayDateToken) },
      };
      if (filterQuery) {
        query.$and = filterQuery;
      }
      return query;
    }),
  );

  destroyed$ = new Subject<void>();

  jobRequestsLoading$ = this.jobRequestQuery$.pipe(
    switchMap((query) => {
      return MeteorObservable.subscribe('jobRequests', query).pipe(
        map(() => false),
        startWith(true),
      );
    }),
    distinctUntilChanged(),
    rxNgZoneRun(this.ngZone),
  );

  dataReady$ = combineLatest([
    this.dispoCustomer$,
    this.loggedInUser$,
    this.jobRequestsLoading$,
  ]).pipe(
    map(
      ([dispoCustomer, loggedInUser, jobRequestsLoading]) =>
        dispoCustomer && loggedInUser && !jobRequestsLoading,
    ),
    startWith(false),
    distinctUntilChanged(),
    rxNgZoneRun(this.ngZone),
    shareReplay(1),
    // debounce fixes flaky test that detaches because url params change on load which loads & unloads the list
    debounceTime(200),
    tap(() => {
      this.cdr.detectChanges();
    }),
  );

  jobRequests$?: Observable<Job[]> = combineLatest([
    this.jobRequestQuery$,
    this.jobRequestsLoading$,
  ]).pipe(
    switchMap(([query, loading]) => {
      if (loading) {
        return of([] as Job[]);
      }
      return Jobs.find(query, {});
    }),
    shareReplay(1),
    rxNgZoneRun(this.ngZone),
    takeUntil(this.destroyed$),
  );

  jobRequestsByDate$: Observable<{ [date: string]: Job[] }> | undefined =
    this.jobRequests$?.pipe(
      map((requests) =>
        requests.reduce((acc, request) => {
          if (!acc[request.date]) {
            acc[request.date] = [];
          }
          acc[request.date].push(request);
          return acc;
        }, {} as { [date: string]: Job[] }),
      ),
      map((jobRequestsByDate) =>
        // sort jobs by shift start time
        Object.fromEntries(
          Object.entries(jobRequestsByDate).map(([date, jobs]) => [
            date,
            jobs.sort((a, b) => a.shifts[0].from - b.shifts[0].from),
          ]),
        ),
      ),
      shareReplay(1),
      takeUntil(this.destroyed$),
    );

  departmentAccess$ = this.userService.readyUser$.pipe(
    map((user) => {
      let departmentAccess: DepartmentPermissions | undefined;
      if (
        user.role === RoleEnum.DISPO &&
        user.dispoPermissions?.dispoAccess?.featurePermissions
          ?.hasRequestsAccess
      ) {
        let customer = Customers.findOne(user.customerId);
        // if no specific departments are set it means they have full access
        departmentAccess = user.dispoPermissions?.departments || {
          readAll: true,
          writeAll: true,
        };
        if (customer && !customer.enableJobRequestProcess) {
          this.router.navigate(['dashboard']);
        }
      } else if (user.role === RoleEnum.REQUESTER) {
        // if no specific departments are set it means they have full access
        departmentAccess = user.departmentAccess || {
          readAll: true,
          writeAll: true,
        };
      } else {
        this.router.navigate(['dashboard']);
      }
      return departmentAccess;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  jobRequestSubscription?: Subscription;

  departmentQueryParams$ = combineLatest([
    this.route.queryParams,
    this.organisationService.readyDepartments$,
  ]).pipe(
    map(([params, departments]): string => {
      let param = params['departmentId'];
      let department = departments?.find(
        (department) => department.id === param,
      );
      this.selectDepartment(department);
      return param;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
  );

  disableFilterSave$ = this.dispoCustomer$.pipe(
    map((customer) => customer?.disableDispoFilterSave),
    distinctUntilChanged(),
  );

  userDefaultFilters$ = combineLatest([
    this.activeTabId$,
    this.loggedInUser$,
  ]).pipe(
    map(([tabId, user]) => {
      return user?.jobRequestFilters?.[tabId];
    }),
    shareReplay(1),
    rxDistinctJSONStringify(),
  );

  initDefaultFiltersEffect$ = combineLatest([
    this.userDefaultFilters$,
    this.route.queryParams,
  ]).pipe(
    take(1),
    tap(([defaultFilters, queryParams]) => {
      const hasFilterParams =
        queryParams.departmentId ||
        queryParams.jobType ||
        queryParams.shift ||
        queryParams.demandReason;
      // dont load default filter params when coming from a link with existing departmentId or other filters
      if (hasFilterParams) {
        return;
      }
      this.mergeQueryParams({
        departmentId: defaultFilters?.departmentId || null,
        jobType: defaultFilters?.jobType || null,
        shift: defaultFilters?.shift || null,
        demandReason: defaultFilters?.demandReason || null,
      });
    }),
  );

  activeView$ = new BehaviorSubject<'tiles' | 'list'>(
    (localStorage.getItem('jobRequestActiveView') as 'tiles' | 'list') ||
      'tiles',
  );

  columnResizeManager = new ColumnResizeManager(
    'jobRequestColumnWidths',
    this.ngZone,
  );

  columns$ = combineLatest([
    this.dispoCustomer$,
    this.columnResizeManager.columnWidths$,
    this.activeTabId$,
  ]).pipe(
    map(([customer, columnWidths, activeTabId]) => {
      let columns: Column[] = [...defaultJobRequestColumns];

      // For approve tab, add date and department columns
      if (activeTabId === JobRequestTabId.APPROVE) {
        columns.splice(1, 0, { key: 'date', label: 'Datum', width: '120px' });
        columns.splice(3, 0, {
          key: 'department',
          label: 'Abteilung',
          width: '150px',
        });
        columns.splice(4, 0, {
          key: 'organisation',
          label: 'Organisation',
          width: '125px',
          smallSpacing: true,
        });
      }

      // Apply custom widths from localStorage
      const mappedColumns = columns.map((col) => ({
        ...col,
        width: columnWidths[col.key] || col.width,
      }));

      // Ensure CSS rules exist for all column keys with their widths
      this.columnResizeManager.ensureColumnKeysExist(mappedColumns);

      return mappedColumns;
    }),
    shareReplay(1),
  );

  constructor(
    private ngZone: NgZone,
    private userService: UserService,
    private organisationService: OrganisationService,
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.prepareMonths();
    // cant be in constructor to not interrupt the initial navigation which would cause the sidenav to not get the navigation event
    this.initDefaultFiltersEffect$.subscribe();

    this.jobRequestSubscription =
      MeteorObservable.subscribe('jobRequests').subscribe();
  }

  ngOnDestroy() {
    if (this.jobRequestSubscription) {
      this.jobRequestSubscription.unsubscribe();
    }
    this.columnResizeManager.cleanup();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  selectDepartment(department?: Department) {
    if (department) {
      let availableShiftsByDay: {
        [weekDay: number]: { shift: ShiftEnum; shifts: Shift[] }[];
      } = {};

      for (let i = 0; i < 7; i++) {
        availableShiftsByDay[i] = [];
        let weekDay = getWeekDayEnumByIndex(i);

        // Group shifts by their type (early, late, etc)
        const shiftsByType = department.shifts.reduce((acc, shift) => {
          if (
            shift.specificWeekDays &&
            !shift.specificWeekDays.includes(weekDay)
          ) {
            return acc;
          }

          if (!acc[shift.shift]) {
            acc[shift.shift] = [];
          }
          acc[shift.shift].push(shift);
          return acc;
        }, {} as { [key in ShiftEnum]: Shift[] });

        // Convert grouped shifts to array format
        Object.entries(shiftsByType).forEach(([shiftType, shifts]) => {
          availableShiftsByDay[i].push({
            shift: Number(shiftType) as ShiftEnum,
            shifts: shifts,
          });
        });
      }

      this.shiftsOfDepartment = availableShiftsByDay;

      this.selectedDepartment$.next(department);
    } else {
      this.shiftsOfDepartment = {};
      this.selectedDepartment$.next(undefined);
    }
  }

  createJobRequest(date: string, shift: Shift, departmentId: string) {
    this.router.navigate(['job-requests/new'], {
      queryParams: {
        date: date,
        shift: shift.shift,
        departmentId,
        shiftId: shift.id,
      },
    });
  }

  toggleFilters(): void {
    this.filtersCollapsed$.next(!this.filtersCollapsed$.value);
    localStorage.setItem(
      'jobRequestFiltersCollapsed',
      this.filtersCollapsed$.value.toString(),
    );
  }

  switchTab(tabId: JobRequestTabId) {
    this.loggedInUser$.pipe(take(1)).subscribe((user) => {
      const defaultFilters = user?.jobRequestFilters?.[tabId];
      this.mergeQueryParams({
        tab: tabId,
        departmentId: defaultFilters?.departmentId || null,
        jobType: defaultFilters?.jobType || null,
        shift: defaultFilters?.shift || null,
        demandReason: defaultFilters?.demandReason || null,
      });
    });
  }

  mergeQueryParams(queryParams: Params) {
    this.router
      .navigate([], {
        queryParamsHandling: 'merge',
        queryParams,
      })
      .then();
  }

  prepareMonths(): void {
    this.months = [];
    this.months.push(this.prepareMonth(startOfDay(new Date())));
    times(this._monthsInFuture, (index: number) => {
      this.months.push(
        this.prepareMonth(addMonths(startOfMonth(new Date()), index + 1)),
      );
    });
  }

  prepareMonth(inputStartDate: Date): JobRequestMonth {
    let startDate = startOfDay(inputStartDate);
    let endDate = endOfMonth(inputStartDate);
    let currentWeekStart = startOfISOWeek(inputStartDate);
    let weeks: JobRequestWeek[] = [];

    if (isBefore(currentWeekStart, inputStartDate)) {
      currentWeekStart = inputStartDate;
    }

    if (endDate && currentWeekStart) {
      while (isBefore(currentWeekStart, endDate)) {
        weeks.push(this.prepareWeek(currentWeekStart));
        currentWeekStart = startOfISOWeek(addWeeks(currentWeekStart, 1));
      }
    }

    return { weeks, startDate, endDate, collapsed: false };
  }

  prepareWeek(inputStartDate: Date): JobRequestWeek {
    let startDate = startOfDay(inputStartDate);
    let endDate = startOfDay(inputStartDate);
    let dates: string[] = [];

    const endOfWeekDate = startOfDay(endOfISOWeek(inputStartDate));
    const endOfMonthDate = startOfDay(endOfMonth(inputStartDate));

    if (isAfter(endOfWeekDate, endOfMonthDate)) {
      endDate = endOfMonthDate;
    } else {
      endDate = endOfWeekDate;
    }

    let currentDayStart = new Date(startDate);
    while (isBefore(currentDayStart, addDays(endDate, 1))) {
      dates.push(format(currentDayStart, dbDayDateToken));

      currentDayStart = addDays(currentDayStart, 1);
    }
    return { dates, startDate, endDate };
  }

  saveJobRequestFilters() {
    this.activeTabId$.pipe(take(1)).subscribe((tabId: JobRequestTabId) => {
      const queryParams = this.route.snapshot.queryParams;

      Meteor.call(
        'saveJobRequestFilters',
        tabId,
        {
          jobType: queryParams['jobType'] || null,
          shift: queryParams['shift'] || null,
          demandReason: queryParams['demandReason'] || null,
          departmentId: queryParams['departmentId'] || null,
        },
        (error: any) => {
          if (error) {
            console.error('Error saving job request filters:', error);
            this.toastService.add({
              type: 'ERROR',
              message: 'Failed to save filters',
              timeout: 5000,
            });
          } else {
            this.toastService.add({
              type: 'SUCCESS',
              message: 'savedDefaultFilter',
              timeout: 5000,
            });
          }
        },
      );
    });
  }

  toggleView() {
    setTimeout(() => {
      const newView = this.activeView$.value === 'tiles' ? 'list' : 'tiles';
      this.activeView$.next(newView);
      localStorage.setItem('jobRequestActiveView', newView);
    }, 50);
  }

  onResizeStart = (event: MouseEvent, columnKey: string) =>
    this.columnResizeManager.onResizeStart(event, columnKey);

  trackByColumnKey = (index: number, column: Column) => column.key;
  trackByMonth = (index: number, month: JobRequestMonth) =>
    month.startDate.getTime();
  trackByWeek = (index: number, week: JobRequestWeek) =>
    week.startDate.getTime();
  trackByShiftGroup = (
    index: number,
    shiftGroup: { shift: ShiftEnum; shifts: Shift[] },
  ) => shiftGroup.shift;
  trackByShift = (index: number, shift: Shift) => shift.id;
  trackByJobRequest = (index: number, jobRequest: Job) => jobRequest._id;
}
